import { useEffect, useState } from "react";

import CountryCode from "@mapmycustomers/shared/enum/CountryCode";
import TerritoryLevel from "@mapmycustomers/shared/enum/TerritoryLevel";
import AreaRegion from "@mapmycustomers/shared/types/territory/AreaRegion";
import LookupData, { RawLookupData } from "@mapmycustomers/shared/types/territory/LookupData";

import getBoundaryUrl from "@app/scene/map/utils/getBoundaryUrl";

const emptyLookupData: LookupData = {
  [TerritoryLevel.ADM1]: {},
  [TerritoryLevel.ADM2]: {},
};

const rawLookupToData = (rawLookup: RawLookupData) => {
  const lookupData: LookupData = { [TerritoryLevel.ADM1]: {}, [TerritoryLevel.ADM2]: {} };
  (Object.keys(rawLookup) as AreaRegion["id"][]).forEach((level1Id) => {
    const level2Ids = Object.keys(rawLookup[level1Id]) as AreaRegion["id"][];
    let allLevel3Ids = [] as AreaRegion["id"][];
    lookupData[TerritoryLevel.ADM1][level1Id] = {
      [TerritoryLevel.ADM2]: level2Ids,
      [TerritoryLevel.ADM3]: [],
    };
    level2Ids.forEach((level2Id) => {
      const level3Ids = rawLookup[level1Id][level2Id];
      allLevel3Ids = [...allLevel3Ids, ...level3Ids];
      if (!lookupData[TerritoryLevel.ADM2][level2Id]) {
        lookupData[TerritoryLevel.ADM2][level2Id] = {
          [TerritoryLevel.ADM1]: [],
          [TerritoryLevel.ADM3]: [],
        };
      }
      lookupData[TerritoryLevel.ADM2][level2Id][TerritoryLevel.ADM1].push(level1Id);
      lookupData[TerritoryLevel.ADM2][level2Id][TerritoryLevel.ADM3] = Array.from(
        new Set([...level3Ids, ...lookupData[TerritoryLevel.ADM2][level2Id][TerritoryLevel.ADM3]])
      );
    });
    lookupData[TerritoryLevel.ADM1][level1Id][TerritoryLevel.ADM3] = Array.from(
      new Set(allLevel3Ids)
    );
  });

  return lookupData;
};

const cache: Partial<Record<CountryCode, Promise<LookupData>>> = {};

const useLookupData = (
  countryCode: CountryCode,
  active: boolean = true
): [lookup: LookupData, loading: boolean, error: undefined | unknown] => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(undefined);
  const [lookup, setLookup] = useState<LookupData>(emptyLookupData);

  // this useEffect loads data, it was the first useLookup called for this countryCode
  useEffect(() => {
    if (!active || cache[countryCode]) {
      return;
    }

    setLoading(true);
    cache[countryCode] = new Promise((resolve, reject) => {
      fetch(getBoundaryUrl("lookup", countryCode))
        .then((response) => response.json())
        .then((json: RawLookupData) => {
          // Transform lookup data so it's easier to consume
          resolve(rawLookupToData(json));
        })
        .catch(reject)
        .finally(() => {
          setLoading(false);
        });
    });
  }, [active, countryCode]);

  // this useEffect only waits for data to be loaded, that's a path of the 2nd and other
  // useLookupData hooks
  useEffect(() => {
    if (!cache[countryCode]) {
      return;
    }
    setLoading(true);
    cache[countryCode]!.then(setLookup, setError).finally(() => setLoading(false));
  }, [countryCode]);

  return [lookup, loading, error];
};

export default useLookupData;
